import React from 'react'
import { useNavigate, useParams } from 'react-router';
import { FileAddOutlined, FolderAddOutlined, UploadOutlined, FileZipOutlined } from '@ant-design/icons';
import { Modal, Upload, Button, UploadProps, Empty } from 'antd'
import { RcFile } from 'antd/es/upload';
import Dragger from 'antd/es/upload/Dragger';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';
import { removeFileFromList, setDocumentsType, setIsAddedViaDragger } from 'store/slices/dataDocumentsSlice';
import { closeUploadModal, handleUploadFiles } from 'store/reducers/documentsCreator';
import { useAppDispatch, useAppSelector } from 'store/hook';
import { DocumentsType, IWithWebkitPath } from 'interfaces';
import UploadingComponent from 'components/UploadingComponent/UploadingComponent';
import { getTheme } from 'helpers/getTheme';
import { prepareStructure } from './structureHelper';
import ItemElement from './ItemElement';
import classes from './UploadModal.module.scss';

type UploadModalProps = {
  open: boolean
  entityName?: string;
  uploadProps: UploadProps<any>;
  fileList: IWithWebkitPath[];
  folderUploadProps: UploadProps<any>;
  title?: string;
  beforeUpload?: (file: RcFile, allFiles: RcFile[], type?: string) => Promise<boolean>;
  checkAllFileNames?: (allFiles: RcFile[]) => Promise<void>
}

export default function UploadModal({
  open,
  entityName,
  uploadProps,
  fileList,
  folderUploadProps,
  title,
  beforeUpload,
  checkAllFileNames,
}: UploadModalProps) {
  const [foldersFileCount, setFoldersFileCount] = React.useState(0);
  // const [activeKeys, setActiveKeys] = React.useState<any>({});
  // const [activeKeysArr, setActiveKeysArr] = React.useState<string[]>([]);

  const draggerWrapRef = React.useRef<any | null>(null);

  const { isUploadingDocuments, displayedDragger, messageApi } = useAppSelector((store) => store.windowState);
  const { dataRoom } = useAppSelector((store) => store.dataRoom);
  const { documentsType, isAddedViaDragger } = useAppSelector((store) => store.documents);  

  const currentFolderId = useParams().folderId || null;
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const isConfidential = documentsType === 'confidentialFiles';
  const isNeedToChooseUploadMethod = isAddedViaDragger && dataRoom?.agreement_enable && !documentsType && dataRoom?.user_status === 2;
  const onUpload = () => dispatch(handleUploadFiles({t, navigate}));

  const structureForRender = prepareStructure(fileList);
  
  React.useEffect(() => {    
    const isAddedFolder = structureForRender.map(file => file.type).includes('folder');
    const numberOfFoldersFiles = structureForRender.filter(file => file.type === 'folder');
    const isThereNewFolders = foldersFileCount < numberOfFoldersFiles.length;
    setFoldersFileCount(numberOfFoldersFiles.length);
    
    if (isAddedFolder && !currentFolderId && isThereNewFolders) {
      messageApi?.warning(t('Documents.modals.upload.permissionsWarning'), 10);
    };
  }, [structureForRender.length]);

  React.useEffect(() => {
    if (draggerWrapRef.current) {
      const fileInput = draggerWrapRef.current?.querySelector('input[type="file"]');      
      if (fileInput) fileInput.setAttribute('data-testid', 'upload-file-input')
    }
  }, [draggerWrapRef.current]);
  
  // React.useEffect(() => {
  //   for (let key in activeKeys) {
  //     if (activeKeys[key][0]) {
  //       if (!activeKeysArr.includes(key)) {
  //         setActiveKeysArr(prev => [...prev, key]);
  //       }
  //     } else {
  //       setActiveKeysArr(prev => prev.filter(el => el !== key))
  //     }
  //   }
  // }, [activeKeys]);  

  const onDeleteItem = (e: React.MouseEvent<HTMLDivElement, MouseEvent>, itemUids: string[]) => {
    e.stopPropagation();
    e.preventDefault();
    dispatch(removeFileFromList(itemUids));
  };  

  const IsSafari = () => {    
    const is_safari = navigator.vendor === 'Apple Computer, Inc.';
    return is_safari
      ? t('Documents.modals.upload.onlyFolders')
      : '';
  };

  const uploadFiles = (fileType: DocumentsType) => {
    dispatch(setIsAddedViaDragger(false));
    dispatch(setDocumentsType(fileType));
    const rcFileList = fileList as RcFile[];
    checkAllFileNames && checkAllFileNames(rcFileList);
  };

  React.useEffect(() => {
    if (fileList.length && !isNeedToChooseUploadMethod && isAddedViaDragger) {      
      uploadFiles(isConfidential ? 'confidentialFiles' : 'files');
      dispatch(setIsAddedViaDragger(false));
    }
  }, [fileList, isNeedToChooseUploadMethod, isAddedViaDragger]);

  return (
    <Modal 
      styles={{ body: { maxHeight: '90vh' } }}
      width={isNeedToChooseUploadMethod ? '60%' : '95%'}
      title={`${title || t('Documents.modals.upload.files')}: ${entityName}`}
      open={open}
      centered={true}
      onCancel={() => dispatch(closeUploadModal({}))}
      footer={false}
      className={cn(classes.modalUpload, getTheme())}
    >
      {
        isNeedToChooseUploadMethod
          ? (
            <div className={classes.uploadMethod}>
              <div className={classes.titleMethod}>{t('Documents.modals.upload.chooseUploadMethod')}</div>
              <div className={classes.buttonsMethod}>
                <Button
                  onClick={() => uploadFiles('files')}
                  type='primary'
                  icon={<UploadOutlined />}
                >
                  {t('Documents.modals.upload.uploadSimple')}
                </Button>

                <Button
                  onClick={() => uploadFiles('confidentialFiles')}
                  type='primary'
                  icon={<UploadOutlined />}
                >
                  {t('Documents.modals.upload.uploadConfident')}
                </Button>
              </div>
            </div>
          )
          : (
            <div className={classes.uploadWrap}>
              <div className={classes.menu}>
                <div className={classes.upload}>
                  {folderUploadProps.directory && (
                    <Upload
                      {...folderUploadProps}
                    >
                      <div className={classes.uploadButton}>
                        <FolderAddOutlined className={classes.uploadIcon} />
                        <span className={classes.uploadButtonText}>
                          {t('Documents.modals.upload.folder')}
                          <br/>
                          {IsSafari()}
                        </span>
                      </div>
                    </Upload>
                  )}
                  <Upload
                    showUploadList={false}
                    {...uploadProps}
                  >
                    <div className={classes.uploadButton}>
                      <FileAddOutlined className={classes.uploadIcon}/>
                      <span className={classes.uploadButtonText}>{t('Documents.modals.upload.select')}</span>
                    </div>
                  </Upload>
                  <Upload
                    showUploadList={false}
                    accept='.zip' //.rar
                    {...uploadProps}
                    beforeUpload={(fileItem, fileList) => beforeUpload
                      ? beforeUpload(fileItem, fileList, 'anarchive')
                      : uploadProps.beforeUpload!(fileItem, fileList)
                    }
                  >
                    <div className={classes.uploadButton}>
                      <FileZipOutlined className={classes.uploadIcon}/>
                      <span className={classes.uploadButtonText}>{t('Documents.modals.upload.archive')}</span>
                    </div>
                  </Upload>
                </div>
      
                <div className={classes.uploadFooter}>
                  <Button
                    disabled={fileList?.length === 0}
                    type='primary'
                    onClick={onUpload}
                    data-testid='upload-submit-btn'
                  >
                    {t('Documents.management.upload')}
                  </Button>
                </div>
              </div>

              <div
                className={classes.itemsWrap}
                style={{ display: !structureForRender[0] ? 'none' : 'block' }}
              >
                {
                  structureForRender.sort((a, b) => b.type.localeCompare(a.type))
                    .map((item, i) => (
                      <ItemElement
                        key={item.name + i}
                        item={item}
                        isConfidential={isConfidential}
                        onDeleteItem={onDeleteItem}
                        // activeKeys={activeKeys}
                        // setActiveKeys={setActiveKeys}
                        // activeKeysArr={activeKeysArr}
                      />
                    ))
                }
              </div>
              
              <div ref={draggerWrapRef} className={classes.uploadDraggerArea} style={{display: structureForRender[0] && 'none'}}>
                { !displayedDragger &&
                  <Dragger
                    style={{ backgroundColor: 'transparent' }}
                    className={classes.dragger}
                    showUploadList={false}
                    accept='.doc, .docx, .xml, application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document, .pdf, image/*, video/*, audio/*, .txt'
                    {...uploadProps}
                  >
                    <div className={classes.uploadFiles} data-testid='upload-dropzone'>
                      <div className={classes.uploadScreen}>
                        <Empty
                          rootClassName={classes.emptyElement}
                          image={Empty.PRESENTED_IMAGE_SIMPLE}
                          description={t('Documents.modals.upload.dropText')}
                        />
                      </div>
                    </div>
                  </Dragger>
                }
              </div>
            </div>
          )
      }

      {isUploadingDocuments && 
        <UploadingComponent />
      }
    </Modal>
  );
}
